import React, {
    createContext,
    useContext,
    useCallback,
    useEffect,
    useState,
    useRef,
    useMemo,
} from 'react';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import differenceInDays from 'date-fns/difference_in_days';

import {
    getDishesFromBasketSections,
} from 'app/utils/dish';
import {
    parseCategories, parseDishes, parseMenuType, parseTags,
} from 'app/utils/basketSchema';


import BASKET_QUERY from 'app/graphql/network/basket/basketQuery';
import {
    ADD_ITEM_TO_CART,
    ADD_ITEMS_TO_CART_BY_ID_LIST,
    ADD_ITEMS_TO_CART_BY_SCHEMA,
} from 'app/graphql/network/basket/basketMutations';

import { useAddToBasketMutation } from 'app/views/Menu/Menu/hooks';
import { ConfigContext } from './ConfigProvider';
import { FiltersContext } from './FiltersProvider';
import { paramToHeaderLinkSetter } from './utils/setPeriodToHeaderLink';


const getDishesToRemoveByCity = (sections, availableBasketDishIds) => {
    const basketDishes = getDishesFromBasketSections(sections);

    const enabledByCity = availableBasketDishIds;

    return basketDishes
        .filter((d) => !enabledByCity.includes(d.id))
        .map((d) => d.id);
};


export const BasketContext = createContext({
    basket: { sections: [] },
    updateBasket: () => { },
});

export function BasketProvider(props) {
    const { children } = props;

    const { period, initialPeriod } = useContext(FiltersContext);

    const {
        dishesData,
        availableBasketDishIds,
        autofillingDishIds,
        defaultTrialScenario: configTrialScenario,
        areAvaliableDishesPersistentForPeriods,
        basketFillingParams,
        pageId,
    } = useContext(ConfigContext);

    const { handleAddToBasketByidListMutation } = useAddToBasketMutation();

    const [trialScenario, setTrialScenario] = useState(configTrialScenario);

    // TODO: переделать на текстовое поле с несколькими значениями
    const [isBasketCleared, setIsBasketCleared] = useState(false);
    const [isBasketPrepared, setIsBasketPrepared] = useState(false);
    const basketIntializedStateRef = useRef({ isInitialized: false });

    const [isBasketRefetching, setIsBasketRefetching] = useState(false);

    const basketQuery = useQuery(
        BASKET_QUERY,
        {
            variables: { period, trial_scenario: trialScenario },
            fetchPolicy: 'network-only',
            onCompleted: ({ cart }) => {
                // Устанавливает query параметр period в ссылку в header'e
                if (cart) paramToHeaderLinkSetter(!cart?.sections?.length)(cart);
            },
            context: {
                message: 'lp:query:BasketProvider',
            },
        },
    );

    // TODO: Костыль Apollo-3 подумать как сделать нормально
    basketQuery.data = basketQuery.data ?? basketQuery.previousData;

    const [basketMutation] = useMutation(ADD_ITEM_TO_CART);
    const [addItemsToCartByIdList] = useMutation(ADD_ITEMS_TO_CART_BY_ID_LIST);
    const [addItemsToCartBySchema] = useMutation(ADD_ITEMS_TO_CART_BY_SCHEMA);

    const updateBasket = useCallback(async ({
        dishId,
        portions,
        onCompletedCallback = () => { },
    }) => {
        setIsBasketRefetching(true);

        const dishData = dishesData ? dishesData[dishId] : null;
        const dishIds = (dishData && dishData.dishes && dishData.dishes.length) ? dishData.dishes : [dishId];

        await Promise.all(dishIds.map((id) => {
            const variables = {
                period,
                dish_id: id,
                portions,
                source_name: window.location.pathname?.replace(/^\/+|\/+$/g, ''),
            };
            return basketMutation({ variables, onCompleted: onCompletedCallback });
        }));
        await basketQuery.refetch();

        setIsBasketRefetching(false);
    }, [basketQuery, basketMutation, dishesData, period]);

    const fillTrialBasket = useCallback(async ({
        dishIds,
        trialScenario: trialScenarioArg,
        period: periodArg,
    }) => {
        setIsBasketRefetching(true);

        const variables = {
            period: periodArg || period,
            dishes: dishIds,
            trialScenario: trialScenarioArg,
            source_name: window.location.pathname?.replace(/^\/+|\/+$/g, ''),
        };

        await addItemsToCartByIdList({ variables });
        await basketQuery.refetch();

        setIsBasketRefetching(false);
    }, [basketQuery, addItemsToCartByIdList, period]);

    const refetchBasket = useCallback(async (variables) => {
        setIsBasketRefetching(true);

        if (variables) {
            const deliveryDate = new Date(variables.delivery_date);
            const periodDate = new Date(initialPeriod);
            const daysDiff = differenceInDays(deliveryDate, periodDate);

            if (daysDiff >= 7) {
                if (configTrialScenario) {
                    await fillTrialBasket({
                        trialScenario: configTrialScenario,
                        dishIds: autofillingDishIds,
                        period: variables.delivery_date,
                    });
                } else if (areAvaliableDishesPersistentForPeriods) {
                    const basketDishes = getDishesFromBasketSections(basketQuery.data.cart.sections);

                    const dishIds = basketDishes.reduce((acc, dish) => {
                        const [defaultPortions] = dish.portions;
                        const idCountInMutation = dish.portion / defaultPortions;

                        const ids = Array.from(new Array(idCountInMutation)).map(() => dish.id);

                        return [
                            ...acc,
                            ...ids,
                        ];
                    }, []);

                    await fillTrialBasket({
                        trialScenario: configTrialScenario,
                        dishIds,
                        period: variables.delivery_date,
                    });
                }
            }
        }

        const basketResp = await basketQuery.refetch(variables);
        setIsBasketRefetching(false);

        return [basketResp, basketResp];
    }, [
        basketQuery, setIsBasketRefetching, areAvaliableDishesPersistentForPeriods,
        autofillingDishIds, configTrialScenario, fillTrialBasket, initialPeriod,
    ]);

    // const checkDishesInBasketByTag = (sections, tagId) => {
    //     const allTagsByDish = mapAllDishesTagsInBasket(sections, true);

    //     const dishesWithoutTargetTag = Object.entries(allTagsByDish)
    //         .filter(([_, allDishTags]) => !allDishTags.some((tag) => tag.id === tagId))
    //         .map(([dishId]) => dishId);

    //     const dishesWithTargetTag = Object.entries(allTagsByDish)
    //         .filter(([_, allDishTags]) => allDishTags.some((tag) => tag.id === tagId))
    //         .map(([dishId]) => dishId);
    //     return { dishesWithTargetTag, dishesWithoutTargetTag };
    // };

    // const filterDishesForNYPeriods = useCallback(() => {
    //     const sections = basketQuery.data?.cart?.sections;

    //     if (!sections) return;

    //     const {
    //         dishesWithTargetTag: dishesWithNYTag,
    //     } = checkDishesInBasketByTag(sections, NY_DISHES);
    //     const {
    //         dishesWithTargetTag: dishesWithNYDessertsTag,
    //     } = checkDishesInBasketByTag(sections, NY_DESSERT);

    //     const isNYPage = pageId === 'blyuda-dlya-novogodnego-stola' || pageId === 'christmas' || pageId === 'old_ny';

    //     let dishesCollectionToDelete = [];
    //     if (!isNYPage) {
    //         dishesCollectionToDelete = dishesWithNYTag;
    //     }

    //     if (dishesCollectionToDelete.length) {
    //         const allDishesToDelete = sections
    //             .map((s) => s.items)
    //             .flat()
    //             .filter((dish) => {
    //                 const hasDessertTag = dishesWithNYDessertsTag.includes(dish.dish_id);
    //                 if (hasDessertTag) {
    //                     return null;
    //                 }
    //                 return dishesCollectionToDelete.includes(dish.dish_id);
    //             });
    //         const deletingDishesArray = allDishesToDelete.map((dish) => ({ dish_id: dish.dish_id, portions: 0 }));
    //         mutateItemInCart(
    //             { dishes: deletingDishesArray },
    //         );
    //     }
    // }, [basketQuery.data, mutateItemInCart, pageId]);

    const basketContextValue = useMemo(() => {
        const v = {
            basket: basketQuery.data ? basketQuery.data.cart : { sections: [] },
            promocodeConditions: basketQuery.data ? basketQuery.data.cart.discount_conditions : {},
            updateBasket,
            refetchBasket,
            isBasketRefetching,
            setTrialScenario,
            trialScenario,
        };
        return v;
    }, [basketQuery.data, isBasketRefetching, refetchBasket, trialScenario, updateBasket]);

    useEffect(() => {
        const hasBasketData = basketQuery.data || basketQuery.error;


        if (isBasketCleared) return;
        if (!hasBasketData) return;

        if (basketIntializedStateRef.current.isInitialized) return;
        basketIntializedStateRef.current.isInitialized = true;

        const { basket: { sections } } = basketContextValue;

        if (!availableBasketDishIds) {
            setIsBasketCleared(true);
            setIsBasketPrepared(true);
            return;
        }

        const parsedCategories = parseCategories(window.location);
        const parsedDishes = parseDishes(window.location);
        const parsedMenuType = parseMenuType(window.location);
        const parsedTags = parseTags(window.location);

        const isNeededFillBasketBySchema = Boolean(parsedCategories || parsedDishes || basketFillingParams);
        const hasDishesWhiteList = availableBasketDishIds && availableBasketDishIds.length > 0;

        const dishIdsToRemove = hasDishesWhiteList
            ? getDishesToRemoveByCity(
                sections,
                availableBasketDishIds,
            )
            : [];

        const mutations = dishIdsToRemove.map((dishId) => {
            const variables = {
                period,
                dish_id: dishId,
                portions: 0,
            };
            return basketMutation({ variables });
        });

        Promise.all([mutations]).then(async () => {
            setIsBasketCleared(true);

            if (autofillingDishIds) {
                if (configTrialScenario) {
                    await fillTrialBasket({
                        trialScenario: configTrialScenario,
                        dishIds: autofillingDishIds,
                    });
                    await basketQuery.refetch();
                } else {
                    await Promise.all(autofillingDishIds.map(async (dishId) => {
                        const portionInitialCount = (dishesData && dishesData[dishId])
                            ? dishesData[dishId].dishCountStep
                            : 1;
                        const r = await updateBasket({ dishId, portions: portionInitialCount });
                        return r;
                    }));
                }
            } else {
                if (isNeededFillBasketBySchema) {
                    const dishes = parsedDishes || basketFillingParams?.dishes;
                    const menuType = parsedMenuType || basketFillingParams?.menuType;
                    const tags = parsedTags || basketFillingParams?.tags;
                    const schema = parsedCategories || basketFillingParams?.schema;

                    const params = new URLSearchParams(window.location.search);

                    const saveAddedDishes = basketFillingParams?.saveAddedDishes || params.get('save');

                    const localStorageKeys = Object.keys({ ...localStorage });
                    localStorageKeys
                        .filter((key) => key.includes('upsellDishes'))
                        .forEach((key) => localStorage.removeItem(key));

                    if (dishes) {
                        const currentMutation = saveAddedDishes
                            ? () => handleAddToBasketByidListMutation({ dishes })
                            : () => addItemsToCartByIdList({
                                variables: {
                                    period,
                                    trialScenario,
                                    dishes,
                                },
                            });
                        await currentMutation();
                    } else {
                        await addItemsToCartBySchema({
                            variables: {
                                period,
                                menuType,
                                tags: tags || [],
                                schema,
                            },
                        });
                    }
                }

                await basketQuery.refetch();
            }

            setIsBasketPrepared(true);
        });
    }, [basketQuery, basketContextValue, isBasketCleared]);

    // useEffect(
    //     () => {
    //         filterDishesForNYPeriods();
    //     },
    //     [basketQuery.data, filterDishesForNYPeriods],
    // );

    return (
        <BasketContext.Provider value={basketContextValue}>
            {isBasketPrepared && children}
        </BasketContext.Provider>
    );
}

BasketProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};
